import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Circle,
  FeatureGroup,
  MapContainer,
  TileLayer,
  Tooltip,
  useMap,
  GeoJSON,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import {
  GeoJSONFeature,
  GeoJSONFeatureCollection,
  MapFilterData,
  salesFilterData,
  State,
} from "pages/sales-forecast/salesForecastTypes";
import {
  cityLatLong,
  getLatLongByNames,
  stateLatLong,
} from "pages/sales-forecast/locLatLong";
import stateData from "pages/sales-forecast/usStates.json";
import countryData from "pages/sales-forecast/countries.json";
import L, { map } from "leaflet";
import { ToggleViewState } from "pages/forecast/forecastTypes";
import Legend from "./Legend";

interface WorldMapProps {
  salesMapData: any;
  mapFilterData: MapFilterData;
  mapRef: React.RefObject<any>;
  setToggleView: Dispatch<SetStateAction<ToggleViewState>>;
  salesFilterData: salesFilterData;
  fetchSalesData: (filterData: salesFilterData) => Promise<void>;
  setSalesFilterData: Dispatch<SetStateAction<salesFilterData>>;
  setMapFilterData: Dispatch<SetStateAction<MapFilterData>>;
}

interface CityInfo {
  lat: number;
  lng: number;
  forecast_qt: number;
  sales_qt: number;
  name: string;
  color: string;
}

interface RecenterAutomaticallyProps {
  lat: number;
  lng: number;
}

const transformCityCoordinates = (salesMapData: any) => {
  const cityHighlights = salesMapData
    ?.flatMap((state: any) =>
      state.cities.map((cityData: any) => {
        const city = cityLatLong.find((city) => city.name === cityData.city);

        if (city) {
          return {
            lat: city.lat,
            lng: city.long,
            forecast_qt: cityData.forecast_quantity,
            sales_qt: cityData.sales_quantity,
            name: city.name,
            color: "#5627D9",
          };
        }

        return null; // Return null if '99 WEB' or city not found
      })
    )
    .filter((city: any) => city !== null); // Filter out null entries

  return cityHighlights;
};

const transformStateCoordinates = (
  salesMapData: any[]
): GeoJSONFeatureCollection => {
  // Flatten the states array from each country in `salesMapData`
  const allStatesData = salesMapData.flatMap((country) => country.states);

  // Map each state to its corresponding GeoJSON feature
  const features: GeoJSONFeature[] = allStatesData
    .map((item) => {
      const state = stateData.features.find(
        (feature: any) => feature.properties.name === item.state
      );

      // If state exists in `stateData`, create a GeoJSON feature with updated properties
      if (state) {
        return {
          type: state.type,
          geometry: {
            type: state.geometry.type,
            coordinates: state.geometry.coordinates,
          },
          properties: {
            forecast_qt: item.forecast_quantity,
            sales_qt: item.sales_quantity,
            name: state.properties.name,
            density: state.properties.density,
            locType: "state",
            apiReqName: item.state,
          },
        };
      }
      return null; // State not found
    })
    .filter((feature): feature is GeoJSONFeature => feature !== null); // Filter out null values

  return {
    type: "FeatureCollection",
    features,
  };
};

const transformCountryCoordinates = (
  salesMapData: any[]
): GeoJSONFeatureCollection => {
  const features: GeoJSONFeature[] = salesMapData
    .map((item) => {
      const country = countryData.features.find(
        (feature) =>
          item.country === "US" &&
          feature.properties.name === "United States of America"
      );

      if (country) {
        return {
          type: country.type,
          geometry: {
            type: country.geometry.type,
            coordinates: country.geometry.coordinates, // Ensure this matches the expected format of a GeoJSON Polygon
          },
          properties: {
            forecast_qt: item.forecast_quantity,
            sales_qt: item.sales_quantity,
            name: country.properties.name,
            locType: "country",
          },
        };
      }

      return null; // State not found in coordinates
    })
    .filter((feature): feature is GeoJSONFeature => feature !== null); // Type predicate to ensure only GeoJSONFeature types are returned

  // Return the GeoJSON feature collection
  return {
    type: "FeatureCollection",
    features,
  };
};

const RecenterAutomatically: React.FC<
  RecenterAutomaticallyProps & { zoom: number }
> = ({ lat, lng, zoom }) => {
  const map = useMap();

  useEffect(() => {
    map.setView([lat, lng], zoom, { animate: true });
  }, [lat, lng, zoom]); // Ensure zoom is also a dependency
  return null;
};

interface CityRadiusType{
name: string,
radius: number

}

const WorldMap: React.FC<WorldMapProps> = ({
  salesMapData,
  mapFilterData,
  mapRef,
  setToggleView,
  salesFilterData,
  fetchSalesData,
  setSalesFilterData,
  setMapFilterData,
}) => {
  const [cities, setCities] = useState<CityInfo[]>([]);
  const [states, setStates] = useState<GeoJSONFeatureCollection | null>(null);
  const [country, setCountry] = useState<GeoJSONFeatureCollection | null>(null);
  const [homePosition, setHomePosition] = useState<[number, number]>([45, -60]);
  const [mapKey, setMapKey] = useState<boolean>(false);
  const [extremes, setExtremes] = useState<{ min: number; max: number }>({
    min: 0,
    max: 0,
  });
  const [cityWithRadius, setCityWithRadius] = useState<CityRadiusType[]>([])

console.log(country)

  useEffect(() => {
 
    if (salesMapData) {
      const hasCities = salesMapData[0]?.hasOwnProperty("cities")
        ? true
        : false;
      setMapKey(hasCities);
      if (hasCities) {
        const data = transformCityCoordinates(salesMapData);
        setCities(data);
        setStates(null); // Clear state data when cities are being used
        setCountry(null);
      } else {
        if (
          salesMapData[0]?.hasOwnProperty("country") &&
          !salesMapData[0]?.hasOwnProperty("states")
        ) {
          const countryData = transformCountryCoordinates(salesMapData);
          setCities([]); // Clear city data when states are being used
          setStates(null);
          setCountry(countryData);

          // Extract all forecast values into a single array
          const forecastQuantities = salesMapData?.map(
            (item: any) => item.forecast_quantity
          );

          // Find the minimum and maximum values in the forecast quantities array
          const minForecast = Math.min(...forecastQuantities);
          const maxForecast = Math.max(...forecastQuantities);

          setExtremes({ ...extremes, min: minForecast, max: maxForecast });
        } else {
          const data = transformStateCoordinates(salesMapData);
          setCities([]);
          setCountry(null);
          setStates(data);
          // Clear city data when states are being used

          // Extract all forecast values into a single array
          const forecastQuantities = salesMapData
            .map((item: any) => {
              const countryForecast = item.forecast_quantity;
              const stateForecasts = item.states.map(
                (state: any) => state.forecast_quantity
              );

              // Combine the country forecast with state forecasts
              return [countryForecast, ...stateForecasts];
            })
            .flat();

          // Find the minimum and maximum values in the forecast quantities array
          const minForecast = Math.min(...forecastQuantities);
          const maxForecast = Math.max(...forecastQuantities);

          setExtremes({ ...extremes, min: minForecast, max: maxForecast });
        }
      }

      let getLatLongs: [number, number];
      if (mapFilterData.city_name.length > 0) {
        getLatLongs = getLatLongByNames(mapFilterData.city_name, "city");
      } else {
        getLatLongs = [37.8, -96];
      }

      setHomePosition(getLatLongs);
    }
  }, [salesMapData, mapFilterData]); // Dependency on both salesMapData and mapFilterData

  const mapPolygonColorToDensity = (density: number) => {
    const { min, max } = extremes;

    let thresholds;

    // Handle the case where min and max are the same
    if (min === max) {
      thresholds = [min]; // Only one threshold in this case
    } else {
      thresholds = Array.from({ length: 4 }, (_, i) => {
        return min + ((max - min) / 3) * i;
      });
    }

    // Map colors to thresholds
    if (
      density > thresholds[3] ||
      (thresholds.length === 1 && density >= thresholds[0])
    )
      return "#240046";
    if (density > thresholds[2]) return "#3c096c";
    if (density > thresholds[1]) return "#5a189a";
    return "#7B2CBF";
  };

  useEffect(() => {
    // Create a style element for custom styles
    const style = document.createElement("style");
    style.innerHTML = `
      .custom-tooltip {
        pointer-events: auto; /* Allows interaction with the tooltip */
        z-index: 1000; /* Make sure it is above other elements */
      }

      .tooltip-link {
        color: blue; /* Style your link as needed */
        text-decoration: underline; /* Example styling */
        cursor: pointer; /* Indicate that it’s clickable */
      }
    `;
    document.head.appendChild(style); // Append the style to the document head

    return () => {
      document.head.removeChild(style); // Clean up on unmount
    };
  }, []);

  // Style function for polygons based on forecast_qt property
  const style = (feature: any) => ({
    fillColor: mapPolygonColorToDensity(feature.properties.forecast_qt), // Your function to map color
    weight: 0,
    opacity: 0,
    color: "violet",
    dashArray: "1",
    fillOpacity: 0.8,
  });

  const highlightFeature = (e: any) => {
    const layer = e.target;

    // layer.setStyle({
    //   weight: 5,
    //   color: 'violet',
    //   dashArray: '2',
    //   fillOpacity: 1,

    // });

    layer.bringToFront();

    const tooltip = L.tooltip({ className: "custom-tooltip" }) // Add a custom class
      .setLatLng(e.latlng)
      .setContent(
        `<div>
          <h4>${layer.feature.properties.name}</h4>
          <p><b>Forecast</b>: $${layer.feature.properties.forecast_qt.toLocaleString(
            "en-US"
          )}</p>
          <p><b>History</b>: $${layer.feature.properties.sales_qt.toLocaleString(
            "en-US"
          )}</p>
          <p><a href="#" id="tooltip-link">${
            layer.feature.properties.locType === "state" ? "view detail" : ""
          }</a></p>
        </div>`
      )
      .openOn(mapRef.current);

    setTimeout(() => {
      const link = document.getElementById("tooltip-link");

      if (link) {
        link.addEventListener("click", (event) => {
          event.preventDefault();

          const newSalesFilterData = {
            ...salesFilterData,
            state: [layer.feature.properties.apiReqName],
          };
          const newMapFilterData = {
            ...mapFilterData,
            state: [layer.feature.properties.apiReqName],
          };

          setToggleView((prev) => {
            return { ...prev, gridView: true, chartView: false };
          });

          fetchSalesData(newSalesFilterData);
          setSalesFilterData(newSalesFilterData);
          setMapFilterData(newMapFilterData);
        });
      }
    }, 0);

    layer.tooltip = tooltip; // Store the tooltip reference
  };

  const resetHighlight = (e: any) => {
    const layer = e.target;

    if (
      !e.originalEvent.relatedTarget ||
      !e.originalEvent.relatedTarget.classList.contains("custom-tooltip")
    ) {
      if (layer.tooltip) {
        mapRef.current.closeTooltip(layer.tooltip);
      }
    }
  };

  const onEachFeature = (feature: any, layer: any) => {
    feature.properties.locType === "country"
      ? layer.on({
          click: highlightFeature,
          mouseout: resetHighlight,
        })
      : layer.on({
          click: highlightFeature,
          mouseout: resetHighlight,
        });
  };



  useEffect(() => {
    setCityWithRadius(() => {
      const maxRadius = 50;
  
      // Extract city names & forecast_qt values properly
      const qtValues = cities.map((location) => ({
        name: location.name,
        qt: location.forecast_qt,
      }));
  
      const maxQT = Math.max(...qtValues.map((item) => item.qt)); // Find max QT
  
      // Compute radius while keeping city names
      return qtValues.map((item) => ({
        name: item.name,
        radius: ((maxRadius * Math.sqrt(item.qt)) / Math.sqrt(maxQT))*3000,
      }));
    });
  }, [cities]);

  return (
    <section className="forcast-geo-section">
      <h5>Forecast by Geography</h5>
      <MapContainer
        ref={mapRef}
        center={homePosition}
        zoom={mapKey ? 5 : 4}
        scrollWheelZoom={false}
        attributionControl={false}
        id="map"
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

        {mapKey ? (
          cities.map((location, idx) => {

            const getRadius = cityWithRadius.find(city=>city.name === location.name)?.radius ?? 0
          
            return (
              <FeatureGroup key={idx}>
                <Circle
                  center={[location.lat, location.lng]}
                  fillColor={location.color}
                  radius={getRadius}
                  fillOpacity={0.7}
                  weight={0}
                />
                <Tooltip
                  direction="top"
                  offset={[0, 20]}
                  opacity={1}
                  permanent={false}
                >
                  <div>
                    <h4>{location.name}</h4>
                    <p>
                      <b>Forecast</b>: $
                      {location.forecast_qt.toLocaleString("en-US")}
                    </p>
                    <p>
                      <b>History</b>: $
                      {location.sales_qt.toLocaleString("en-US")}
                    </p>
                  </div>
                </Tooltip>
              </FeatureGroup>
            );
          })
        ) : states && country === null ? (
          <>
            <GeoJSON
              data={states}
              style={style}
              onEachFeature={onEachFeature}
              key={"stateMap"}
            />
            <Legend mergedGeoJSON={states} extremes={extremes} />
          </>
        ) : (
          country &&
          states === null && (
            <>
              <GeoJSON
                data={country}
                style={style}
                onEachFeature={onEachFeature}
                key={"countryMap"}
              />
              <Legend mergedGeoJSON={country} extremes={extremes} />
            </>
          )
        )}

        <RecenterAutomatically
          lat={homePosition[0]}
          lng={homePosition[1]}
          zoom={4}
        />
      </MapContainer>
    </section>
  );
};

export default WorldMap;

// const captureMapAsImage = () => {
//   if (mapRef.current) {
//     const mapElement = mapRef.current.getContainer(); // Get the map container

//     html2canvas(mapElement, { useCORS: true, allowTaint: true }).then(canvas => {
//       const imgData = canvas.toDataURL('image/png');
//       const link = document.createElement('a');
//       link.href = imgData;
//       link.download = 'map.png';
//       link.click();
//     }).catch(error => {
//       console.error('Error capturing map:', error);
//     });
//   }
// };
// console.log(states)
